import type { AccountType, NavItemAccess } from "@/components/app-sidebar/types";
import { canHoldPayoutAccount, resolveAccountTypes } from "@shared/auth/account-audience";

export interface AccountTypeFlags {
  buyer?: boolean;
  vendor?: boolean;
  freightCarrier?: boolean;
  prospect?: boolean;
}

export interface DashboardAudience {
  accountTypes: AccountType[];
  primaryAccountType: AccountType | undefined;
  isFreightOnly: boolean;
}

export const EMPTY_DASHBOARD_AUDIENCE: DashboardAudience = {
  accountTypes: [],
  primaryAccountType: undefined,
  isFreightOnly: false,
};

export function resolveDashboardAudience(
  flags: AccountTypeFlags | undefined,
  legacyAccountType: AccountType | undefined,
): DashboardAudience {
  const accountTypes = resolveAccountTypes(flags, legacyAccountType);
  return {
    accountTypes,
    primaryAccountType:
      legacyAccountType && accountTypes.includes(legacyAccountType)
        ? legacyAccountType
        : accountTypes[0],
    isFreightOnly:
      accountTypes.length === 1 && accountTypes[0] === "freight_carrier",
  };
}

export function isVendorAccount(audience: DashboardAudience): boolean {
  return audience.accountTypes.includes("vendor");
}

export function isBuyerAccount(audience: DashboardAudience): boolean {
  return audience.accountTypes.includes("buyer");
}

export function isBuyerOnlyAccount(audience: DashboardAudience): boolean {
  return (
    isBuyerAccount(audience) &&
    !isVendorAccount(audience) &&
    !audience.accountTypes.includes("freight_carrier")
  );
}

export function isFreightCarrierAccount(audience: DashboardAudience): boolean {
  return audience.accountTypes.includes("freight_carrier");
}

export function canAccessMarketplacePayouts(
  audience: DashboardAudience,
): boolean {
  return canHoldPayoutAccount(audience.accountTypes);
}

export function canAccessFreightBilling(audience: DashboardAudience): boolean {
  return audience.accountTypes.includes("freight_carrier");
}

export function shouldHideForAudience(
  item: NavItemAccess,
  audience: DashboardAudience,
): boolean {
  const { accountTypes } = audience;

  if (accountTypes.length === 0) {
    return Boolean(item.requiredAccountTypes);
  }

  if (
    item.hideForAccountTypes &&
    accountTypes.every((type) => item.hideForAccountTypes?.includes(type))
  ) {
    return true;
  }

  return Boolean(
    item.requiredAccountTypes &&
    !accountTypes.some((type) => item.requiredAccountTypes?.includes(type)),
  );
}

export function shouldHideDashboardNavItem(
  item: NavItemAccess,
  audience: DashboardAudience,
  canManageBilling: boolean,
): boolean {
  return (
    shouldHideForAudience(item, audience) ||
    (Boolean(item.requiresBillingManager) && !canManageBilling)
  );
}
